// Strings shared across the site chrome (header nav, footer, common CTAs).
// Keyed by locale; the typed shape guarantees every locale defines every key.

import { type Locale } from "./config";

export type SharedStrings = {
  cta: {
    readMore: string;
    contactUs: string;
    contact: string;
  };
  nav: {
    home: string;
    services: string;
    industries: string;
    career: string;
    partners: string;
    about: string;
    contact: string;
    // services submenu
    allServices: string;
    projektledelse: string;
    byggeledelse: string;
    bygherreraadgivning: string;
    tidsplanlaegning: string;
    projektoekonomi: string;
    projektassistance: string;
    shutdown: string;
    hse: string;
    siteCompliance: string;
    // industries submenu
    allIndustries: string;
    pharma: string;
    industri: string;
    datacentre: string;
    energi: string;
    hospitaler: string;
    kontor: string;
    transport: string;
    // career submenu
    openPositions: string;
    workplace: string;
    meetSupporter: string;
    // about submenu
    aboutMi: string;
    esg: string;
    viden: string;
    misupportIt: string;
  };
  footer: {
    servicesHeading: string;
    aboutHeading: string;
    contactHeading: string;
    contactBlurb: string;
    rights: string;
    svcByggeledelse: string;
    svcProjektledelse: string;
    svcHse: string;
    svcIkt: string;
    svcPlanning: string;
    svcControlling: string;
    svcSiteCompliance: string;
    freeSafetyCheck: string;
    career: string;
    legalCookie: string;
    legalPersondata: string;
  };
  languageSwitcherAria: string;
};

export const shared: Record<Locale, SharedStrings> = {
  da: {
    cta: { readMore: "Læs mere", contactUs: "Kontakt os", contact: "Kontakt" },
    nav: {
      home: "Forside",
      services: "Ydelser",
      industries: "Brancher",
      career: "Karriere",
      partners: "Samarbejdspartnere",
      about: "Om os",
      contact: "Kontakt",
      allServices: "Alle ydelser",
      projektledelse: "Projektledelse",
      byggeledelse: "Byggeledelse",
      bygherreraadgivning: "Bygherrerådgivning",
      tidsplanlaegning: "Tidsplanlægning",
      projektoekonomi: "Projektøkonomi",
      projektassistance: "Projektassistance",
      shutdown: "Planlægning og ledelse af shutdowns",
      hse: "Sikkerhedsledelse (HSE)",
      siteCompliance: "Site Compliance",
      allIndustries: "Alle brancher",
      pharma: "Pharma, Biotech & Life Science",
      industri: "Industri & Produktion",
      datacentre: "Datacentre",
      energi: "Energi & Infrastruktur",
      hospitaler: "Hospitaler & Sundhed",
      kontor: "Kontor & Hovedsæder",
      transport: "Transport & Logistik",
      openPositions: "Ledige stillinger",
      workplace: "MI Support som arbejdsplads",
      meetSupporter: "Mød en MI Supporter",
      aboutMi: "Om MI Support",
      esg: "ESG",
      viden: "Viden",
      misupportIt: "MI Support IT",
    },
    footer: {
      servicesHeading: "Ydelser",
      aboutHeading: "Om os",
      contactHeading: "Kontakt",
      contactBlurb: "Har I spørgsmål eller brug for en konsulent? Vi står klar til at hjælpe.",
      rights: "Alle rettigheder forbeholdes.",
      svcByggeledelse: "Byggeledelse",
      svcProjektledelse: "Projektledelse",
      svcHse: "HSE-ledelse",
      svcIkt: "IKT-ledelse",
      svcPlanning: "Planning",
      svcControlling: "Controlling",
      svcSiteCompliance: "Site Compliance",
      freeSafetyCheck: "Gratis sikkerhedstjek",
      career: "Karriere",
      legalCookie: "Cookie- og privatlivspolitik",
      legalPersondata: "Persondatapolitik",
    },
    languageSwitcherAria: "Skift sprog",
  },
  en: {
    cta: { readMore: "Read more", contactUs: "Contact us", contact: "Contact" },
    nav: {
      home: "Home",
      services: "Services",
      industries: "Industries",
      career: "Careers",
      partners: "Partners",
      about: "About",
      contact: "Contact",
      allServices: "All services",
      projektledelse: "Project management",
      byggeledelse: "Construction management",
      bygherreraadgivning: "Client consultancy",
      tidsplanlaegning: "Scheduling",
      projektoekonomi: "Project economics",
      projektassistance: "Project assistance",
      shutdown: "Shutdown planning and management",
      hse: "Safety management (HSE)",
      siteCompliance: "Site Compliance",
      allIndustries: "All industries",
      pharma: "Pharma, Biotech & Life Science",
      industri: "Industry & Manufacturing",
      datacentre: "Data centers",
      energi: "Energy & Infrastructure",
      hospitaler: "Hospitals & Healthcare",
      kontor: "Offices & Headquarters",
      transport: "Transport & Logistics",
      openPositions: "Open positions",
      workplace: "MI Support as a workplace",
      meetSupporter: "Meet a MI Supporter",
      aboutMi: "About MI Support",
      esg: "ESG",
      viden: "Knowledge",
      misupportIt: "MI Support IT",
    },
    footer: {
      servicesHeading: "Services",
      aboutHeading: "About",
      contactHeading: "Contact",
      contactBlurb: "Do you have questions or need a consultant? We're ready to help.",
      rights: "All rights reserved.",
      svcByggeledelse: "Construction management",
      svcProjektledelse: "Project management",
      svcHse: "HSE management",
      svcIkt: "ICT management",
      svcPlanning: "Planning",
      svcControlling: "Controlling",
      svcSiteCompliance: "Site Compliance",
      freeSafetyCheck: "Free safety check",
      career: "Careers",
      legalCookie: "Cookie & privacy policy",
      legalPersondata: "Data policy",
    },
    languageSwitcherAria: "Change language",
  },
  de: {
    cta: { readMore: "Mehr erfahren", contactUs: "Kontaktieren Sie uns", contact: "Kontakt" },
    nav: {
      home: "Startseite",
      services: "Leistungen",
      industries: "Branchen",
      career: "Karriere",
      partners: "Partner",
      about: "Über uns",
      contact: "Kontakt",
      allServices: "Alle Leistungen",
      projektledelse: "Projektleitung",
      byggeledelse: "Baumanagement",
      bygherreraadgivning: "Bauherrenberatung",
      tidsplanlaegning: "Terminplanung",
      projektoekonomi: "Projektökonomie",
      projektassistance: "Projektassistenz",
      shutdown: "Shutdown-Planung und -Leitung",
      hse: "Sicherheitsmanagement (HSE)",
      siteCompliance: "Site Compliance",
      allIndustries: "Alle Branchen",
      pharma: "Pharma, Biotech & Life Science",
      industri: "Industrie & Produktion",
      datacentre: "Rechenzentren",
      energi: "Energie & Infrastruktur",
      hospitaler: "Krankenhäuser & Gesundheit",
      kontor: "Büros & Hauptsitze",
      transport: "Transport & Logistik",
      openPositions: "Offene Stellen",
      workplace: "MI Support als Arbeitgeber",
      meetSupporter: "Treffen Sie einen MI Supporter",
      aboutMi: "Über MI Support",
      esg: "ESG",
      viden: "Wissen",
      misupportIt: "MI Support IT",
    },
    footer: {
      servicesHeading: "Leistungen",
      aboutHeading: "Über uns",
      contactHeading: "Kontakt",
      contactBlurb: "Haben Sie Fragen oder benötigen Sie einen Berater? Wir helfen Ihnen gerne.",
      rights: "Alle Rechte vorbehalten.",
      svcByggeledelse: "Baumanagement",
      svcProjektledelse: "Projektleitung",
      svcHse: "HSE-Management",
      svcIkt: "IKT-Management",
      svcPlanning: "Planung",
      svcControlling: "Controlling",
      svcSiteCompliance: "Site Compliance",
      freeSafetyCheck: "Kostenlose Sicherheitsprüfung",
      career: "Karriere",
      legalCookie: "Cookie- und Datenschutzrichtlinie",
      legalPersondata: "Datenschutzbestimmungen",
    },
    languageSwitcherAria: "Sprache wechseln",
  },
};
